import { ArrowLeft, Copy } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type Permission, type RoleDetail, roleInputSchema } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { FormActions } from '#client/components/ui/FormActions';
import { Modal } from '#client/components/ui/Modal';
import { Page } from '#client/components/ui/Page';
import { PageLoading, PageMessage } from '#client/components/ui/PageStatus';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import { useCan } from '#client/hooks/useCan';
import { useMutation } from '#client/hooks/useMutation';
import { useResourceForm } from '#client/hooks/useResourceForm';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { ENDPOINT } from '#client/lib/endpoints';
import { RESOURCE, routes } from '#client/lib/routes';
import { useAuth } from '#client/providers/AuthProvider';
import { useToast } from '#client/providers/ToastProvider';
import { PermissionHistory } from './PermissionHistory';
import { PermissionMatrix } from './PermissionMatrix';
import { cloneRole } from './role.service';
import { type SetField, useFormValues } from '#client/hooks/useFormValues';

const PATH = RESOURCE.roles;

/**
 * One role: its name and the permission matrix.
 *
 * The same screen views and edits — `/roles/:id` is read-only for someone with
 * `role.view` alone, and editable for `role.manage`. A permission matrix has no
 * "detail" worth having apart from itself.
 */
export function RoleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useResourceItem<RoleDetail>(PATH, id);
  const back = (
    <Button icon={<ArrowLeft />} aria-label="Back" onClick={() => void navigate(PATH)} />
  );
  const heading = id === undefined ? 'New role' : (data?.name ?? 'Role');

  if (loading) return <PageLoading title={heading} backAction={back} fullWidth />;
  if (error !== undefined) {
    return (
      <PageMessage title={heading} backAction={back} fullWidth>
        {error}
      </PageMessage>
    );
  }

  // Keyed by id so moving between roles remounts with fresh state.
  return <RoleForm key={id ?? 'new'} id={id} record={data} back={back} />;
}

interface RoleFormValues {
  name: string;
  description: string;
  permissions: Set<Permission>;
}

function RoleDetailsCard({
  values,
  onFieldChange,
  errors,
  disabled,
}: {
  values: RoleFormValues;
  onFieldChange: SetField<RoleFormValues>;
  errors: Partial<Record<string, string>>;
  disabled: boolean;
}) {
  return (
    <Card>
      <div className="c-form-grid">
        <TextField
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(event) => onFieldChange('name', event.target.value)}
          disabled={disabled}
          required
        />
        <TextArea
          label="Description"
          value={values.description}
          error={errors.description}
          onChange={(event) => onFieldChange('description', event.target.value)}
          disabled={disabled}
          rows={2}
        />
      </div>
    </Card>
  );
}

function RoleForm({
  id,
  record,
  back,
}: {
  id: string | undefined;
  record: RoleDetail | undefined;
  back: ReactNode;
}) {
  const access = useRoleAccess(record);
  const { values, set } = useFormValues<RoleFormValues>({
    name: record ? record.name : '',
    description: record?.description ?? '',
    permissions: new Set<Permission>(record?.permissions),
  });
  const [isCloning, setIsCloning] = useState(false);
  const form = useResourceForm({ path: PATH, id, schema: roleInputSchema, noun: 'Role' });

  return (
    <Page
      {...pageHeading(record)}
      backAction={back}
      fullWidth
      secondaryActions={
        access.canClone ? (
          <Button icon={<Copy />} onClick={() => setIsCloning(true)}>
            Duplicate
          </Button>
        ) : undefined
      }
    >
      <form
        onSubmit={form.handleSubmit({ ...values, permissions: [...values.permissions] })}
        noValidate
      >
        <div className="c-form-stack">
          {form.formError && <Banner tone="critical">{form.formError}</Banner>}
          <RoleNotices record={record} canEdit={access.canEdit} />
          <RoleDetailsCard
            values={values}
            onFieldChange={set}
            errors={form.errors}
            disabled={!access.canEdit}
          />
          <Card
            title="Permissions"
            subtitle="Ticking an action also ticks what it needs; unticking one also unticks what depends on it."
          >
            <PermissionMatrix
              mode="grant"
              selected={values.permissions}
              onChange={access.canEditPermissions ? (next) => set('permissions', next) : undefined}
            />
          </Card>
          {record && <PermissionHistory path={ENDPOINT.roleHistory(record.id)} />}
          {access.canEdit && (
            <FormActions
              cancelTo={PATH}
              submitLabel={id === undefined ? 'Create role' : 'Save role'}
              submitting={form.submitting}
            />
          )}
        </div>
      </form>
      {record && (
        <CloneDialog open={isCloning} source={record} onClose={() => setIsCloning(false)} />
      )}
    </Page>
  );
}

function pageHeading(record: RoleDetail | undefined): { title: string; subtitle: string } {
  if (!record) return { title: 'New role', subtitle: 'A named set of permissions to give staff.' };
  return {
    title: record.name,
    subtitle: record.description ?? `${record.permission_count} permissions`,
  };
}

/**
 * What this viewer may do with this role. The admin role's name is the one
 * thing about it that can change, and only an admin may change it.
 */
function useRoleAccess(record: RoleDetail | undefined) {
  const can = useCan();
  const { user } = useAuth();
  const manages = can('role.manage');
  const isSuper = record?.is_super === true;
  const canEdit = manages && (!isSuper || user?.role.is_super === true);
  return {
    canEdit,
    canEditPermissions: canEdit && !isSuper,
    canClone: manages && record !== undefined && !isSuper,
  };
}

function RoleNotices({ record, canEdit }: { record: RoleDetail | undefined; canEdit: boolean }) {
  if (record?.is_super) {
    return (
      <Banner tone="info" title="This is the admin role">
        It holds every permission, including any added in future, and its permissions cannot be
        changed. Give staff a narrower role instead.
      </Banner>
    );
  }
  if (!record || record.user_count === 0 || !canEdit) return null;
  return (
    <Banner tone="warning">
      {record.user_count === 1
        ? '1 staff member holds this role.'
        : `${record.user_count} staff members hold this role.`}{' '}
      A change applies to them on their next click.
    </Banner>
  );
}

function CloneDialog({
  open,
  source,
  onClose,
}: {
  open: boolean;
  source: RoleDetail;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const mutation = useMutation('Could not duplicate the role.');
  const [name, setName] = useState(`${source.name} (copy)`);

  const handleSubmit = async () => {
    const outcome = await mutation.run(() => cloneRole(source.id, { name }));
    if (!outcome.ok) return;
    toast.success(`Created “${outcome.value.name}”`);
    onClose();
    void navigate(routes.role(outcome.value.id));
  };

  return (
    <Modal
      open={open}
      title={`Duplicate “${source.name}”`}
      onClose={onClose}
      primaryAction={{
        label: 'Duplicate',
        onAction: () => void handleSubmit(),
        loading: mutation.isPending,
      }}
      secondaryAction={{ label: 'Cancel', onAction: onClose }}
    >
      <p>The new role starts with the same {source.permission_count} permissions.</p>
      {mutation.errorMessage && <Banner tone="critical">{mutation.errorMessage}</Banner>}
      <TextField
        label="Name of the new role"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />
    </Modal>
  );
}
